// Shared catalog-entity accessors used by the catalog, api and docs pages.
// Previously copy-pasted (identically) into CatalogTablePage, CatalogPage,
// ApiCardPage, DocsPage and CuratedDocsPage — centralised here so the spec/
// relation shapes are read in exactly one place.
import {
  Entity,
  RELATION_OWNED_BY,
  RELATION_OWNER_OF,
  stringifyEntityRef,
} from '@backstage/catalog-model';

/**
 * Browseable source URL for an entity — the repo/file it was defined from.
 * Reads the standard Backstage location annotations in priority order and strips
 * the `url:` locationType prefix. Returns null when no http(s) source is known
 * (e.g. local dev entities). Used by the "View source" affordance.
 */
export function getSourceUrl(entity: Entity): string | null {
  const ann = entity.metadata.annotations || {};
  const candidates = [
    ann['backstage.io/view-url'],
    ann['backstage.io/source-location'],
    ann['backstage.io/managed-by-location'],
    ann['backstage.io/managed-by-origin-location'],
  ];
  for (const c of candidates) {
    if (!c) continue;
    const url = c.replace(/^url:/, '').trim();
    if (/^https?:\/\//i.test(url)) return url;
  }
  return null;
}

/** Owner short-name from the `ownedBy` relation (e.g. "platform-team"). */
export function getOwner(entity: Entity): string {
  const rel = (entity.relations || []).find(r => r.type === 'ownedBy');
  return rel ? rel.targetRef.split('/').pop() || '' : '';
}

/**
 * Human display label for an owner short-name. Entity ids can't hold an umlaut
 * (metadata.name is ASCII-only), so the org group id is `draeger` while users
 * should see "Dräger". Extend this map for any other id→label prettification.
 * Filter *values* still key off getOwner (the raw id); only the label differs.
 */
const OWNER_LABELS: Record<string, string> = {
  draeger: 'Dräger',
};
export function ownerLabel(owner: string): string {
  return OWNER_LABELS[owner] || owner;
}

/**
 * Normalise a git author name to "First Last". Azure DevOps returns either
 * "Last, First" (web edits) or "First Last" (git config) — collapse both.
 */
export function formatAuthor(raw: string): string {
  const s = (raw || '').trim();
  const m = s.match(/^([^,]+),\s*(.+)$/);
  return m ? `${m[2].trim()} ${m[1].trim()}` : s;
}

/**
 * The doc's last author, stamped by the backend LastAuthorProcessor from the
 * source repo's most recent catalog-info.yaml commit. Null when unknown (e.g.
 * local dev with no Azure token, or a non-ADO entity). name is display-formatted.
 */
export function getAuthor(entity: Entity): { name: string; email: string } | null {
  const a = entity.metadata.annotations ?? {};
  const name = a['draeger.com/last-author'];
  if (!name) return null;
  return { name: formatAuthor(name), email: a['draeger.com/last-author-email'] || '' };
}

/**
 * The last-updated date stamped by the backend LastAuthorProcessor (annotation
 * draeger.com/last-author-date). Returns a locale-formatted date string ("17 Jul 2026")
 * or null when the annotation is absent (local dev, non-ADO entity, etc.).
 * The raw ISO-8601 value comes from the ADO commit author.date field.
 */
export function getLastUpdated(entity: Entity): string | null {
  const raw = entity.metadata.annotations?.['draeger.com/last-author-date'];
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * True when an entity has at least one relation worth drawing a relations graph for
 * (`EntityCatalogGraphCard` on the details screen renders only when this holds).
 *
 * Two kinds of relation don't count:
 *  - relations that point back at the entity ITSELF — an entity always graphs itself,
 *    so a card built from those alone is a single lone node, which is what made the
 *    box look broken on standalone entries; and
 *  - the owner pair (`ownedBy` / `ownerOf`). Practically every entity here is owned by
 *    the one org group `group:default/draeger`, which is deliberately excluded from the
 *    catalog view, so an owner edge adds nothing the About card's Owner row doesn't
 *    already say — and counting it would keep the graph on every page, i.e. hide it
 *    nowhere.
 */
export function hasGraphableRelations(entity: Entity): boolean {
  const self = stringifyEntityRef(entity).toLowerCase();
  return (entity.relations ?? []).some(
    r =>
      r.targetRef.toLowerCase() !== self &&
      r.type !== RELATION_OWNED_BY &&
      r.type !== RELATION_OWNER_OF,
  );
}

/** System short-name from spec.system, falling back to a `partOf` system relation. */
export function getSystem(entity: Entity): string {
  const spec = entity.spec as Record<string, unknown> | undefined;
  if (typeof spec?.system === 'string') return spec.system;
  const rel = (entity.relations || []).find(
    r => r.type === 'partOf' && r.targetRef.startsWith('system:'),
  );
  return rel ? rel.targetRef.split('/').pop() || '' : '';
}

/** spec.lifecycle, or '' when absent. */
export function getLifecycle(entity: Entity): string {
  const lc = (entity.spec as Record<string, unknown> | undefined)?.lifecycle;
  return typeof lc === 'string' ? lc : '';
}

/** spec.type, or '' when absent. */
export function getType(entity: Entity): string {
  const t = (entity.spec as Record<string, unknown> | undefined)?.type;
  return typeof t === 'string' ? t : '';
}

/**
 * The auto-detected "component class" — 'documentation' | 'technical' | '' — stamped by
 * the backend ComponentClassProcessor onto `draeger.com/component-class`. This DevHub is
 * used mostly as a docs system, so most `kind: Component` entities are documentation; this
 * is the axis that tells them apart from real technical components without manual tagging.
 * Only Components carry it, so this returns '' for other kinds and for entities that
 * predate the processor. Keep the annotation key in sync with the backend's
 * COMPONENT_CLASS_ANNOTATION (componentClassModule.ts).
 */
export function getComponentClass(entity: Entity): string {
  return entity.metadata.annotations?.['draeger.com/component-class'] ?? '';
}

/**
 * True when an entity is an unpublished PREVIEW/draft — marked `spec.lifecycle:
 * preview` in its catalog-info.yaml. Preview entities are hidden from the normal
 * catalog/docs listings (revealed only via the internal-only preview toggle) and
 * are excluded server-side from search, the Curator, and the MCP face, so a
 * half-finished guideline can never be mistaken for an official one. Keep this in
 * sync with the backend's `PREVIEW_LIFECYCLE` (previewFilterDecorator.ts).
 */
export function isPreview(entity: Entity): boolean {
  return getLifecycle(entity).toLowerCase() === 'preview';
}

/**
 * Catalog entities suppressed from the user-facing Docs surfaces. These are
 * discovered org-wide by the azureDevOps provider but should not appear in the
 * DevHub:
 *  - `backstage-docs-eval` — internal Backstage evaluation scratch repo (cut per product owner).
 *  - `pdftron` — superseded duplicate of `apryse-pdfnet-sdk` (PDFTron was renamed Apryse).
 *  - `scandit` — thin duplicate of `scandit-barcode-scanner` (the richer canonical entry).
 *  - `devhub-genai-at-draeger` — old "author needed" placeholder seed, superseded by the
 *    real `genai-at-draeger` ("AI at Dräger / GAIA") doc that Home + Docs overview link to.
 * Keyed by metadata.name; reversible (just remove the name to restore visibility).
 */
export const DOC_DENYLIST = new Set<string>([
  'backstage-docs-eval',
  'pdftron',
  'scandit',
  'devhub-genai-at-draeger',
]);

/** True when an entity should be hidden from the Docs catalog (see DOC_DENYLIST). */
export function isDocHidden(entity: Entity): boolean {
  return DOC_DENYLIST.has(entity.metadata.name);
}

/** Route to an entity's detail page: /catalog/<namespace>/<kind>/<name>. */
export function entityPath(entity: Entity): string {
  const namespace = entity.metadata.namespace || 'default';
  const kind = entity.kind.toLowerCase();
  return `/catalog/${namespace}/${kind}/${entity.metadata.name}`;
}

/** Route to an entity's TechDocs page: /docs/<namespace>/<kind>/<name>. */
export function docsPath(entity: Entity): string {
  const namespace = entity.metadata.namespace || 'default';
  const kind = entity.kind.toLowerCase();
  return `/docs/${namespace}/${kind}/${entity.metadata.name}`;
}

/**
 * Returns true when `entity` matches a free-text search `query`.
 * Matches against display title, description, raw metadata name, and TAGS
 * (all case-insensitive).
 *
 * Tags are included deliberately: they are now a search-only signal — the docs page
 * filters on `draeger.com/category` instead — so search is the one place a tag still
 * has to earn its keep. The backend index already folds tags into the searchable text
 * (see search/catalogTagsCollatorModule.ts); this keeps the client-side list search on
 * the same footing rather than being narrower than the global search bar.
 *
 * Used by DocsPage and CuratedDocsPage.
 * NOTE: CatalogPage, CatalogTablePage, and ApiCardPage match only title + description
 * (different behaviour) — leave those callers as-is.
 */
export function entityMatchesSearch(entity: Entity, query: string): boolean {
  const q = query.toLowerCase();
  const title = (entity.metadata.title || entity.metadata.name).toLowerCase();
  const desc = (entity.metadata.description || '').toLowerCase();
  const name = entity.metadata.name.toLowerCase();
  return (
    title.includes(q) ||
    desc.includes(q) ||
    name.includes(q) ||
    (entity.metadata.tags || []).some(tag => tag.toLowerCase().includes(q))
  );
}

/**
 * Resolve a doc/article's catalog icon image to a URL, or null to fall back to a glyph.
 * Teams set it self-serve in their catalog-info.yaml:
 *   metadata.annotations:
 *     draeger.com/docs-icon: simpleicons:sonarqube      # a Simple Icons slug, OR
 *     draeger.com/docs-icon: https://…/logo.png         # any image URL
 */
export function docsIcon(entity: Entity): string | null {
  const raw = entity.metadata.annotations?.['draeger.com/docs-icon'];
  const v = (raw || '').trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;        // any image URL
  if (v.startsWith('/')) return v;               // self-hosted (same-origin) asset
  const slug = v.startsWith('simpleicons:') ? v.slice('simpleicons:'.length) : v;
  return `https://cdn.simpleicons.org/${encodeURIComponent(slug)}`;
}
